// START IMPORTS
// Context imports
import { AppConstants } from '@ltvco/refresh-lib/ctx';
// Theme imports
import { Stack, Text, Divider, Pagination } from '@ltvco/refresh-lib/theme';
// Utility imports
import { pluralizeString } from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import { useSession } from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  useRecentReports,
  stringifyParams,
  generateQueryConfig,
  ReportList,
  type ReportTypeOption,
  type ReportType,
  DeleteAllButton,
  useReportDeleteAll,
  DeleteModal,
  type RecentReportSerialized,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { HistoryRounded } from '@mui/icons-material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useQueryParams } from 'use-query-params';

// App-specific imports
import {
  LoadingReports,
  NullState,
  SearchAndFilterControls,
  RecentReportsCardDescription,
} from './components';
import { hasRunReports } from './utils';
// END IMPORTS

// START CONSTANTS
const queryConfig = generateQueryConfig();
// END CONSTANTS

export const RecentReports: React.FC = () => {
  // START HOOKS_AND_QUERIES
  const {
    config: { reportTypeFilterOptions },
  } = useContext(AppConstants);
  const { session } = useSession();
  const [queryParams, setQueryParams] = useQueryParams(queryConfig);
  const { sort_by, sort_direction } = queryParams;
  const filteredParams = stringifyParams(queryParams);
  const { data: queryData, isLoading } = useRecentReports(filteredParams);

  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const totalReports =
    queryData?.meta?.report_quantities?.total_reports_count || 0;
  const count = Math.ceil(totalReports / 20);
  const reports =
    totalReports === 0 ? [] : (queryData?.reports as RecentReportSerialized[]);
  // END DERIVED_DATA_AND_UTILS

  // START STATE_MANAGEMENT
  const [deleteAllReportsModalOpen, setDeleteAllReportsModalOpen] =
    useState(false);
  const [currentReports, setCurrentReports] = useState(reports);
  const [filterTypes, setFilterTypes] = useState<ReportType[]>(
    queryParams.report_type || []
  );

  const deleteAllMutation = useReportDeleteAll(setCurrentReports);
  // END STATE_MANAGEMENT

  // START EVENT_HANDLERS
  const updateUrlFilter = useCallback(
    (filterTypes: ReportType[]) => {
      setQueryParams({ report_type: filterTypes });
    },
    [setQueryParams, filterTypes]
  );

  const deleteAll = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    deleteAllMutation.mutate();
    setDeleteAllReportsModalOpen(false);
  };

  const handleGoBack = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteAllReportsModalOpen(false);
  };

  const deleteCurrentReport = (permalink: string) =>
    setCurrentReports(
      currentReports.filter(
        (report: RecentReportSerialized) => report.id !== permalink
      )
    );

  const handlePageChange = (page: number) => {
    setQueryParams({ page });
    window.scrollTo(0, 0);
  };
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    setCurrentReports(reports);
  }, [reports]);

  useEffect(() => {
    document.title = 'Recent Reports - PeopleLooker';
  }, []);
  // END EFFECTS

  // START DATA_PREPARATION
  // Remove the monitored report option from the filter options
  delete reportTypeFilterOptions.monitored_report;
  // END DATA_PREPARATION

  // START EARLY_RETURNS
  if (!isLoading && reports.length === 0 && !hasRunReports({ session })) {
    return (
      <Stack spacing={3.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryRounded sx={{ fontSize: '1.25rem' }} />
          <Text variant="h3">Recent Reports</Text>
        </Stack>
        <NullState />
      </Stack>
    );
  }
  // END EARLY_RETURNS

  // START RENDER
  return (
    <>
      <Stack spacing={3.5} padding={2.5} paddingTop={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryRounded sx={{ fontSize: '1.25rem' }} />
          <Text variant="h3">Recent Reports</Text>
        </Stack>
        <SearchAndFilterControls
          filterTypes={filterTypes}
          setFilterTypes={(filterTypes) => {
            setFilterTypes(filterTypes as ReportType[]);
            updateUrlFilter(filterTypes as ReportType[]);
          }}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          reportTypeFilterOptions={
            reportTypeFilterOptions as { [key: string]: ReportTypeOption }
          }
          sort_by={sort_by}
          sort_direction={sort_direction}
        />
        <Stack spacing={1.5} direction="row">
          <Text variant="caption">
            {currentReports.length}{' '}
            {pluralizeString('report', currentReports.length)} viewed
          </Text>
          <Divider
            sx={{ borderColor: 'text.primary' }}
            orientation="vertical"
            flexItem
          />
          <DeleteAllButton
            setDeleteAllReportsModalOpen={setDeleteAllReportsModalOpen}
          />
        </Stack>
        {isLoading ? (
          <LoadingReports />
        ) : (
          <ReportList
            reportList={currentReports}
            deleteCurrentReport={deleteCurrentReport}
            RecentReportsCardDescription={RecentReportsCardDescription}
          />
        )}
        {!isLoading && count > 1 && (
          <Stack
            spacing={2}
            direction={'row'}
            sx={{ justifyContent: 'center' }}
          >
            <Pagination
              page={queryParams.page}
              count={count}
              onChange={(_: any, page: number) => handlePageChange(page)}
              variant="outlined"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-page, & .MuiPaginationItem-ellipsis': {
                  display: 'none',
                },
              }}
            />
          </Stack>
        )}
        <>
          <DeleteModal
            isOpen={deleteAllReportsModalOpen}
            setIsOpen={handleGoBack}
            deleteMutation={deleteAll}
            title={'Delete all reports confirmation'}
            confirmationText="Are you sure you want to delete ALL of your reports?"
            deleteText="Delete all"
          />
        </>
      </Stack>
    </>
  );
  // END RENDER
};
